import { forwardRef } from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils.js';

// shadcn-style Command wrapper around cmdk. Used by TemplatePicker (Comms
// Hub) per TRACON Vision §V2 fix #3 2026-05-20. SearchPalette continues
// to use its own custom cmdk integration in shared/SearchPalette.jsx —
// the API there is divergent enough to keep separate.

const Command = forwardRef(function Command({ className, ...props }, ref) {
  return (
    <CommandPrimitive
      ref={ref}
      className={cn(
        'flex h-full w-full flex-col overflow-hidden rounded-card bg-surface text-text',
        className
      )}
      {...props}
    />
  );
});

const CommandInput = forwardRef(function CommandInput({ className, ...props }, ref) {
  return (
    <div className="flex items-center border-b border-border px-3" cmdk-input-wrapper="">
      <Search className="mr-2 h-4 w-4 shrink-0 text-text-dim" />
      <CommandPrimitive.Input
        ref={ref}
        className={cn(
          'flex h-10 w-full bg-transparent py-2 text-sm outline-none placeholder:text-text-dim',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        {...props}
      />
    </div>
  );
});

const CommandList = forwardRef(function CommandList({ className, ...props }, ref) {
  return (
    <CommandPrimitive.List
      ref={ref}
      className={cn(
        'max-h-[320px] overflow-y-auto overflow-x-hidden',
        className
      )}
      {...props}
    />
  );
});

const CommandEmpty = forwardRef(function CommandEmpty(props, ref) {
  return (
    <CommandPrimitive.Empty
      ref={ref}
      className="py-6 text-center text-xs text-text-dim"
      {...props}
    />
  );
});

const CommandGroup = forwardRef(function CommandGroup({ className, heading, ...props }, ref) {
  return (
    <CommandPrimitive.Group
      ref={ref}
      heading={heading}
      className={cn(
        'overflow-hidden p-1 text-text',
        // Heading styling — cmdk renders the heading as a [cmdk-group-heading] div.
        '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1',
        '[&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold',
        '[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider',
        '[&_[cmdk-group-heading]]:text-text-dim',
        className
      )}
      {...props}
    />
  );
});

const CommandSeparator = forwardRef(function CommandSeparator({ className, ...props }, ref) {
  return (
    <CommandPrimitive.Separator
      ref={ref}
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  );
});

const CommandItem = forwardRef(function CommandItem({ className, ...props }, ref) {
  return (
    <CommandPrimitive.Item
      ref={ref}
      className={cn(
        'relative flex cursor-pointer select-none items-center gap-2 rounded-card px-2 py-2 text-sm outline-none',
        // cmdk applies aria-selected on keyboard nav + hover.
        'aria-selected:bg-accent-soft aria-selected:text-text',
        'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
        className
      )}
      {...props}
    />
  );
});

export {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
};
